import { Icon, IconName } from './Icon';
import { Typography } from './Typography';

import { Provider, Viewport, Root, Title, Close } from '@radix-ui/react-toast';
import clsx from 'clsx';
import { forwardRef, ForwardRefRenderFunction, PropsWithChildren, useEffect, useState } from 'react';

export interface ToastProps {
  isOpen: boolean;
  variant: 'success' | 'info' | 'error';
  message: string;
  onClose: () => void;
  duration?: number;
}
export const DEFAULT_DURATION = 3000;

export const ToastProvider: React.FC<PropsWithChildren> = ({ children }) => {
  // avoid:  https://github.com/radix-ui/primitives/issues/3301
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);
  return (
    <Provider duration={DEFAULT_DURATION}>
      {children}
      {isClient && (
        // hotkeyの"F8"は日本語IMEの半角カタカナ変換キーと衝突し、変換中にフォーカスを奪ってしまう。
        // document全体を監視するホットキー自体を無効化する。
        <Viewport hotkey={[]} className="fixed right-0 top-0 z-[99999] flex flex-col items-end gap-y-4 p-4" />
      )}
    </Provider>
  );
};

const getErrorIcon = (variant: 'success' | 'info' | 'error'): IconName => {
  switch (variant) {
    case 'success':
      return 'check-circle-filled';
    case 'info':
      return 'info';
    case 'error':
      return 'warn';
  }
};

const ToastWithRef: ForwardRefRenderFunction<HTMLLIElement, ToastProps> = (
  { isOpen, variant, message, onClose, duration },
  ref
) => {
  return (
    <Root
      open={isOpen}
      duration={duration}
      ref={ref}
      className={clsx(
        'relative flex w-fit min-w-[300px] cursor-move flex-row items-center gap-x-2 rounded bg-sem-d-surf-primary p-3 shadow-md transition-all duration-300 ease-in-out',
        'data-[state=closed]:animate-fadeOut data-[state=open]:animate-toastSlideIn',
        'data-[swipe=cancel]:translate-x-0 data-[swipe=move]:translate-x-full data-[swipe=end]:animate-toastSwipeOut'
      )}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      onEscapeKeyDown={onClose}
    >
      <div className="flex grow gap-x-2">
        <Icon
          name={getErrorIcon(variant)}
          size="24"
          className={clsx({
            'fill-sem-d-icon-success': variant === 'success',
            'fill-sem-d-icon-accent': variant === 'info',
            'fill-sem-d-icon-error': variant === 'error',
          })}
        />
        <Title>
          <Typography size="cat" className="text-sem-d-text-high">
            {message}
          </Typography>
        </Title>
      </div>
      <Close aria-label="Close">
        <div className="rounded bg-none p-2 transition-all duration-300 hover:bg-white hover:bg-opacity-20">
          <Icon name="close" size="16" className="fill-sem-d-icon-high" aria-hidden />
        </div>
      </Close>
    </Root>
  );
};

export const Toast = forwardRef(ToastWithRef);
